import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows, updateRow } from "@/lib/adminApi";
import { REFUND_STATUS, commerceMeta, commerceOptions, type Refund } from "@/lib/adminCommerce";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnAccent,
  btnGhost,
  btnPrimary,
  fieldInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate, formatRupees } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

export default function AdminRefunds() {
  const [rows, setRows] = useState<Refund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [approving, setApproving] = useState<Refund | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Refund>("refunds", "requested_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load refunds.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Refund>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("refunds", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.refund_ref, row.booking_ref || "", row.visitor_name || "", row.reason || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const totals = useMemo(() => {
    const requested = rows.filter((r) => r.status === "requested").length;
    const approved = rows.filter((r) => r.status === "approved" || r.status === "processing").length;
    const completedAmount = rows.filter((r) => r.status === "completed").reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const pendingAmount = rows
      .filter((r) => r.status === "requested" || r.status === "approved")
      .reduce((sum, r) => sum + Number(r.amount || 0), 0);
    return { requested, approved, completedAmount, pendingAmount };
  }, [rows]);

  const columns: Column<Refund>[] = [
    {
      key: "ref",
      header: "Refund",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.refund_ref}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.booking_ref || "—"}</p>
        </div>
      ),
    },
    { key: "visitor", header: "Visitor", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.visitor_name || "—"}</span> },
    {
      key: "amount",
      header: "Amount",
      render: (row) => <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.amount)}</span>,
    },
    { key: "reason", header: "Reason", render: (row) => <span className="text-sm text-foreground-700">{row.reason || "—"}</span> },
    {
      key: "requested",
      header: "Requested",
      render: (row) => <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.requested_at)}</span>,
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...commerceMeta(REFUND_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Commerce"
        title="Refunds"
        subtitle="Review and settle refund requests — each approval shows the booking and refund policy before anything is processed."
      />

      <StatCards
        stats={[
          { label: "Awaiting review", value: totals.requested, icon: "ri-inbox-line", tone: "accent" },
          { label: "Approved / processing", value: totals.approved, icon: "ri-loader-4-line", tone: "secondary" },
          { label: "Pending amount", value: formatRupees(totals.pendingAmount), icon: "ri-time-line", tone: "accent" },
          { label: "Refunded to date", value: formatRupees(totals.completedAmount), icon: "ri-refund-2-line", tone: "primary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by refund, booking, visitor…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...commerceOptions(REFUND_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} refunds</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading refunds…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-refund-2-line"
            title={rows.length === 0 ? "No refunds yet" : "No refunds match"}
            message="Refund requests from visitors will appear here."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.refund_ref || "Refund"}
        subtitle="Refund detail"
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              {selected.status === "requested" || selected.status === "approved" ? (
                <button type="button" onClick={() => setApproving(selected)} className={btnPrimary}>
                  <i className="ri-shield-check-line"></i> Review &amp; approve
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => update(selected.id, { status: "rejected" }, "Refund rejected.")}
                className={btnGhost}
              >
                <i className="ri-close-line"></i> Reject
              </button>
              <button
                type="button"
                onClick={() => update(selected.id, { status: "completed" }, "Refund marked as completed.")}
                className={btnAccent}
              >
                <i className="ri-check-double-line"></i> Mark completed
              </button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <StatusPill {...commerceMeta(REFUND_STATUS, selected.status)} />

            <DetailList
              items={[
                { icon: "ri-calendar-check-line", label: "Booking reference", value: selected.booking_ref || "—" },
                { icon: "ri-user-3-line", label: "Visitor", value: selected.visitor_name || "—" },
                { icon: "ri-money-rupee-circle-line", label: "Refund amount", value: formatRupees(selected.amount) },
                { icon: "ri-file-text-line", label: "Reason", value: selected.reason || "—" },
                { icon: "ri-calendar-line", label: "Requested on", value: formatDate(selected.requested_at) },
              ]}
            />

            {selected.notes ? (
              <Field label="Internal notes">
                <p className="text-sm text-foreground-700 leading-relaxed">{selected.notes}</p>
              </Field>
            ) : null}

            <Field label="Refund status">
              <select
                value={selected.status}
                onChange={(event) => update(selected.id, { status: event.target.value }, "Refund status updated.")}
                className={fieldInput}
              >
                {commerceOptions(REFUND_STATUS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={Boolean(approving)}
        onClose={() => setApproving(null)}
        title="Approve this refund?"
        subtitle="Confirmation"
        footer={
          <>
            <button type="button" onClick={() => setApproving(null)} className={btnGhost}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (approving) update(approving.id, { status: "approved" }, "Refund approved.");
                setApproving(null);
              }}
              className={btnPrimary}
            >
              <i className="ri-check-line"></i> Confirm approval
            </button>
          </>
        }
      >
        {approving ? (
          <>
            <div className="bg-background-100 border border-background-200 rounded-md p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-600">Booking</span>
                <span className="text-foreground-900 font-medium">{approving.booking_ref || "—"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-600">Visitor</span>
                <span className="text-foreground-900 font-medium">{approving.visitor_name || "—"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-600">Reason</span>
                <span className="text-foreground-900 font-medium">{approving.reason || "—"}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2 border-t border-background-200">
                <span className="font-semibold text-foreground-950">Refund amount</span>
                <span className="font-heading text-lg font-semibold text-foreground-950">
                  {formatRupees(approving.amount)}
                </span>
              </div>
            </div>

            <div className="bg-secondary-50 border border-secondary-200 rounded-md p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-secondary-700 mb-2">
                Refund policy
              </p>
              <ul className="text-xs text-foreground-700 space-y-1.5 list-disc pl-4">
                <li>Free cancellation up to 72 hours before the experience — full refund.</li>
                <li>48–72 hours before — 80% refund after operational costs.</li>
                <li>Under 24 hours — no refund except for verified emergencies.</li>
                <li>Approved refunds are processed to the original payment method within 5–7 working days.</li>
              </ul>
            </div>
          </>
        ) : null}
      </Modal>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Always review the policy before approving.
      </p>
      {node}
    </div>
  );
}